import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChangelogEvent } from "@poi-planner/shared";
import { useTripsListStore } from "../store/tripsListStore";
import { useTripStore } from "../store/tripStore";
import { useCollectionsStore } from "../store/collectionsStore";
import { api } from "../services/api";
import { actionToneConfig, themeColors } from "../theme/colors";
import { Button } from "./Button";
import { getActivityControl } from "./activityFeedModel";

function formatRelativeTime(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function ActivityFeed() {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<ChangelogEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<Record<string, string>>({});
  const activeTripId = useTripsListStore((state) => state.activeTripId);

  const loadEvents = useCallback(async () => {
    if (!activeTripId) return;
    setLoading(true);
    try {
      setEvents(await api.getActivityFeed(activeTripId));
    } catch {
      // Keep the last successful feed visible when a background refresh fails.
    } finally {
      setLoading(false);
    }
  }, [activeTripId]);

  useEffect(() => {
    if (open) void loadEvents();
  }, [open, loadEvents]);

  const linkedIds = useMemo(
    () =>
      new Set(
        events
          .map((event) => event.originalActivityId)
          .filter((id): id is string => Boolean(id)),
      ),
    [events],
  );

  const refreshAffectedState = useCallback(
    async (event: ChangelogEvent) => {
      const tasks: Promise<unknown>[] = [
        useTripsListStore.getState().refreshTrips(),
      ];
      if (activeTripId) {
        tasks.push(useTripStore.getState().refreshTrip(activeTripId));
      }
      if (
        event.entityType.startsWith("collection") ||
        typeof event.inverse?.payload.collectionId === "string"
      ) {
        tasks.push(useCollectionsStore.getState().loadCollections());
      }
      await Promise.all(tasks);
    },
    [activeTripId],
  );

  const transition = useCallback(
    async (event: ChangelogEvent, action: "undo" | "redo") => {
      setPendingId(event.id);
      setConflicts((current) => {
        const next = { ...current };
        delete next[event.id];
        return next;
      });
      try {
        await api.transitionActivity(event.id, action);
        await refreshAffectedState(event);
      } catch (error) {
        setConflicts((current) => ({
          ...current,
          [event.id]:
            error instanceof Error
              ? error.message
              : "This activity no longer matches the current item.",
        }));
      } finally {
        await loadEvents();
        setPendingId(null);
      }
    },
    [loadEvents, refreshAffectedState],
  );

  return (
    <>
      <Button
        onClick={() => setOpen(!open)}
        variant="icon"
        icon="history"
        selected={open}
        title="Activity feed"
        aria-label="Activity feed"
      />

      {open && (
        <aside
          aria-label="Activity feed panel"
          className="fixed top-14 right-3 left-3 sm:left-auto sm:w-[408px] max-h-[calc(100vh-72px)] bg-white rounded-xl border border-border shadow-[0_18px_50px_rgba(15,23,42,0.18)] z-[100] flex flex-col overflow-hidden"
        >
          <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-border bg-slate-50/70">
            <div>
              <h3 className="text-sm font-bold text-primary">Activity</h3>
              <p className="mt-0.5 text-xs text-tertiary">
                Saved changes from people and connected assistants
              </p>
            </div>
            <Button
              onClick={() => setOpen(false)}
              variant="ghost"
              icon="close"
              aria-label="Close activity feed"
            />
          </div>

          <div className="custom-scrollbar flex-1 overflow-y-auto py-2">
            {loading && events.length === 0 ? (
              <div className="p-8 text-center text-tertiary text-sm">
                Loading activity…
              </div>
            ) : events.length === 0 ? (
              <div className="p-8 text-center text-tertiary text-sm flex flex-col items-center gap-2">
                <span className="material-symbols-outlined text-3xl opacity-40">
                  history
                </span>
                <span>No saved activity yet</span>
              </div>
            ) : (
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute left-[32px] top-4 bottom-4 w-px bg-slate-200"
                />
                {events.map((event) => {
                  const config = actionToneConfig[event.action] ?? {
                    icon: "info",
                    color: themeColors.neutral,
                    bg: themeColors.subtle,
                  };
                  const control = getActivityControl(
                    event,
                    events,
                    conflicts[event.id],
                  );
                  const changedByAi =
                    event.source === "mcp" || event.actorType === "api_token";
                  const isLinked = Boolean(event.originalActivityId);
                  const isPending = pendingId === event.id;

                  return (
                    <article
                      key={event.id}
                      className={`relative flex gap-3 px-5 py-3 transition-colors hover:bg-slate-50/80 ${
                        isLinked ? "ml-3 border-l-2 border-sky-200" : ""
                      }`}
                    >
                      <div
                        className="relative z-10 size-7 rounded-full ring-4 ring-white flex items-center justify-center shrink-0 mt-px"
                        style={{ background: config.bg }}
                      >
                        <span
                          className="material-symbols-outlined text-sm"
                          style={{ color: config.color }}
                        >
                          {config.icon}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm text-primary leading-snug font-medium">
                            {event.summary}
                          </p>
                          {changedByAi && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-violet-700">
                              <span className="material-symbols-outlined text-[12px]">
                                auto_awesome
                              </span>
                              Changed by AI
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mt-1 text-xs text-tertiary">
                          <span className="font-medium">
                            {changedByAi
                              ? event.actorName ||
                                event.user ||
                                "Connected assistant"
                              : event.actorName || event.user}
                          </span>
                          <span aria-hidden="true">·</span>
                          <time dateTime={event.timestamp}>
                            {formatRelativeTime(event.timestamp)}
                          </time>
                          {event.tool && changedByAi && (
                            <span className="truncate font-mono text-[10px] text-slate-400">
                              {event.tool}
                            </span>
                          )}
                        </div>

                        {control && (
                          <div className="mt-2 flex items-start gap-2">
                            <button
                              type="button"
                              disabled={control.disabled || isPending}
                              onClick={() =>
                                void transition(event, control.action)
                              }
                              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
                            >
                              <span
                                aria-hidden="true"
                                className="material-symbols-outlined text-sm"
                              >
                                {control.action === "undo" ? "undo" : "redo"}
                              </span>
                              {isPending
                                ? "Applying…"
                                : control.action === "undo"
                                  ? "Undo"
                                  : "Redo"}
                            </button>
                            {control.reason && (
                              <p
                                className={`pt-1 text-[11px] leading-snug ${
                                  event.outcome === "conflict" ||
                                  conflicts[event.id]
                                    ? "text-amber-700"
                                    : "text-tertiary"
                                }`}
                              >
                                {control.reason}
                              </p>
                            )}
                          </div>
                        )}

                        {event.outcome === "conflict" &&
                          event.conflictReason && (
                            <p className="mt-1.5 text-[11px] leading-snug text-amber-700">
                              {event.conflictReason}
                            </p>
                          )}
                      </div>
                      {linkedIds.has(event.id) && !isLinked && (
                        <span className="sr-only">Has linked activity</span>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </aside>
      )}
    </>
  );
}
